import store from "../redux-store";
import {
  customerOptOut,
  getLoyaltyDetails,
  redeemPoints,
  getProgramDetails,
  refundLoyaltyPoints,
  resetLoyaltyProviders,
  selectedLoyaltyDetails,
  sendCustomerConsentOtp,
  verifyCustomerConsentOtp,
  verifyLoyaltyOtp,
} from "../redux-store/slices/Loyalty";
import { loyaltyRefundHelper } from "../helpers/loyaltyHelper";
import { fetchDetailedCustomerInfo } from "../helpers/customerHelpers";
import { getLoyaltyDetailsFetchMode } from "./provider";
import { getProgram } from "./program";
import { pointsToAmount } from "./redemption";
import {
  classifyConsentOtpResponse,
  classifyConsentVerifyResponse,
  classifyEnrolWithoutOtpResponse,
} from "./consent";
import {
  selectActiveLoyaltyProvider,
  selectCustomerLoyaltyInfo,
  selectCustomerLoyaltyRefId,
  selectLoyaltyProgramDetails,
  selectProviderLoyaltyDetails,
  selectSaleData,
  selectSelectedLoyaltyProvider,
} from "./selectors";
import type { ConsentOutcome, LoyaltyProvider, RedemptionInput } from "./types";

/**
 * The loyalty flow's IMPERATIVE ADAPTER — the only file here that dispatches.
 *
 * Layering, innermost first. Each layer may use the ones above it and nothing below:
 *
 * ```
 * types · constants          shapes and vocabulary
 * provider · program         pure rules over a provider / a programme
 * redemption · consent       pure rules over a bill / a customer
 * selectors                  the only file naming a redux key
 * index (this file)          dispatch + payload assembly
 * hooks/                     what UI actually calls
 * ```
 *
 * UI should reach for `./hooks` first; these functions exist for callers that are not React —
 * a saga, a middleware effect, another adapter.
 *
 * ⚠️ Every store read happens INSIDE a function. Nothing at module-eval time crosses into a
 * module that is in a cycle with the store (audit checks H/J).
 */

/* ── redemption ─────────────────────────────────────────────────────────────────────── */

/**
 * Fetch what this provider will let the customer redeem against the CURRENT sale, and cache it
 * under `loyaltyDetails[provider.mid]`.
 *
 * The response is per-sale, not per-customer: adding an item can change it. The panel refetches
 * whenever it has no cached entry for the active provider.
 */
export function fetchLoyaltyDetails(provider: LoyaltyProvider, cb?: (res: any) => void): void {
  store.dispatch(
    getLoyaltyDetails({
      postBody: {
        sale: selectSaleData(store.getState()),
        providerId: provider?.mid,
        metaId: provider?.metaId,
        fetch: getLoyaltyDetailsFetchMode(provider?.metaId),
      },
      callback: cb,
    }),
  );
}

/**
 * Tell the slice which provider the redemption APIs should act on.
 *
 * ⚠️ `redeemPoints` and `verifyRedeemOtp` below read the provider from the SLICE, not from their
 * arguments, so this must be in step with whatever the panel is showing before either is called.
 * `useLoyaltyRedemption` keeps it so.
 */
export function setRedemptionProvider(provider: LoyaltyProvider | Record<string, never>): void {
  store.dispatch(selectedLoyaltyDetails(provider));
}

/**
 * Drop every cached balance.
 *
 * ⚠️ Clears the WHOLE `loyaltyDetails` map, not one provider — the reducer has no per-provider
 * reset. Call it when leaving the redemption surface, not on a provider switch.
 */
export function clearLoyaltyDetails(): void {
  store.dispatch(resetLoyaltyProviders());
}

/**
 * The `loyaltyInput` both redemption calls send, computed from the slice's current provider.
 *
 * ⚠️ Passes `providerMetaId`, and must: the rate a provider uses is one of its declared
 * capabilities, so omitting it would price the PAYLOAD off a different rule than the preview the
 * cashier just agreed to.
 */
function buildRedemptionInput(points: number): RedemptionInput {
  const state = store.getState();
  const provider = selectSelectedLoyaltyProvider(state);
  return pointsToAmount(points, {
    providerMetaId: provider?.metaId,
    details: selectProviderLoyaltyDetails(state, provider?.mid),
    program: getProgram(selectLoyaltyProgramDetails(state)),
  });
}

/**
 * Redeem points against the current sale.
 *
 * The response decides what happens next, and the caller must branch on it:
 * `redemptionInfo.authenticate` means the backend sent an OTP and wants `verifyRedeemOtp`;
 * anything else with a `sale` is already applied. `errorCode` / `code` is a failure.
 *
 * ⚠️ **This is an ACTION, not a modal.** It used to be fired from an OTP dialog's mount effect,
 * so applying points that needed no OTP opened a dialog that immediately closed itself, and
 * "resend" re-ran a full redemption.
 */
export function applyLoyaltyPoints(points: number, cb: (res: any) => void): void {
  const state = store.getState();
  const provider = selectSelectedLoyaltyProvider(state);
  store.dispatch(
    redeemPoints({
      postBody: {
        loyaltyInput: buildRedemptionInput(points),
        providerId: provider?.mid,
        metaId: provider?.metaId,
        sale: selectSaleData(state),
      },
      callback: cb,
    }),
  );
}

/**
 * Verify the OTP the redemption call sent. Success carries the updated sale.
 *
 * `collectibleName` rides along so the backend can name the collectible the way the merchant
 * configured it ("Bean", "Star") rather than "points". Undefined when the programme names none —
 * only the programme-publishing partner has one.
 */
export function verifyRedeemOtp(otp: string, points: number, cb: (res: any) => void): void {
  const state = store.getState();
  const provider = selectSelectedLoyaltyProvider(state);
  const collectibleName = getProgram(selectLoyaltyProgramDetails(state))?.collectibleName;
  store.dispatch(
    verifyLoyaltyOtp({
      postBody: {
        sale: selectSaleData(state),
        providerId: provider?.mid,
        metaId: provider?.metaId,
        loyaltyInput: { ...buildRedemptionInput(points), otp, collectibleName },
      },
      callback: cb,
    }),
  );
}

/**
 * Release redeemed points back to the customer (the backend unblocks them).
 *
 * 🔴 Delegates to `loyaltyRefundHelper`, which is **platform-divergent by design** and is the
 * function `docs/flows/LOYALTY_REFUND.md` documents. Do not reimplement the call here: removal
 * of the discount is committed by the refund's returned sale on success and by nothing on
 * failure, and the middleware effects depend on that exact behaviour.
 *
 * @param sale - Refund target. Pass it explicitly on any path where the store may already hold a
 *   different sale; omit it to refund against the live one.
 * @param skipSave - Suppress writing the refunded sale back. For a sale being discarded.
 */
export function removeLoyaltyPoints(
  providerId: string,
  cb?: (res: any) => void,
  { skipSave = false, sale }: { skipSave?: boolean; sale?: any } = {},
): void {
  loyaltyRefundHelper(providerId, cb, skipSave, sale);
}

/** Direct dispatch of the refund action, for callers assembling their own payload. */
export { refundLoyaltyPoints };

/**
 * Fetch the loyalty programme for a channel.
 *
 * ⚠️ Gate the call on `shouldFetchProgramDetails(metaId)` — only a programme-publishing provider
 * has one. `useProgramDetailsFetch` does both.
 */
export function fetchProgramDetails(channelDefId: string): void {
  store.dispatch(getProgramDetails({ channelDefId }));
}

/* ── consent ────────────────────────────────────────────────────────────────────────── */

/** Who and what a consent call is about. */
export interface ConsentRequest {
  phoneNumber: string;
  provider?: LoyaltyProvider | null;
  /**
   * The customer payload to enrol from.
   *
   * ⚠️ Required on the OTP-LESS path and on verify: there is no other call carrying the customer,
   * so this request is what the backend creates and enrols them from. Omitted on the plain
   * send-OTP path, where the verify call carries it.
   */
  customerInfo?: any;
}

/**
 * Send the enrolment-consent OTP — or, when the programme skips OTPs, enrol outright. The raw
 * dispatch: it posts what it is given and hands the envelope back unread.
 *
 * ⚠️ **Prefer `requestEnrolment`.** The same endpoint serves both paths, and `customerInfo`
 * plus the response classification are what differ — pairing those correctly is the whole job,
 * and every surface that did it for itself got some of it wrong. Call this directly only to
 * assemble a request neither path covers.
 */
export function sendConsentOtp(
  { phoneNumber, provider, customerInfo }: ConsentRequest,
  cb: (res: any) => void,
): void {
  store.dispatch(
    sendCustomerConsentOtp({
      dataToPost: {
        phoneNumber,
        providerId: provider?.mid,
        metaId: provider?.metaId,
        ...(customerInfo ? { customerInfo } : {}),
      },
      callback: cb,
    }),
  );
}

/**
 * Verify the consent OTP. Success records the customer's enrolment backend-side.
 *
 * ⚠️ **Prefer `confirmEnrolment`**, which reads the answer and re-reads the customer.
 */
export function verifyConsentOtp(
  { phoneNumber, provider, customerInfo }: ConsentRequest,
  otp: string,
  cb: (res: any) => void,
): void {
  store.dispatch(
    verifyCustomerConsentOtp({
      dataToPost: {
        phoneNumber,
        otp,
        providerId: provider?.mid,
        metaId: provider?.metaId,
        customerInfo,
      },
      callback: cb,
    }),
  );
}

/**
 * What an enrolment attempt ENDED IN. Four states, and only one of them is a failure.
 *
 * `already-consented` is kept distinct from `enrolled` rather than folded into it: both are
 * terminal successes and both mean "stop asking", but a surface that toasts wants to say
 * "consent already provided" rather than claiming it just enrolled them.
 */
export type EnrolmentOutcome = "enrolled" | "already-consented" | "otp-required" | "failed";

export interface EnrolmentRequest extends ConsentRequest {
  /**
   * Which path this is — `!enforcement.enrolOnNext`, i.e. `enforcement.blocksSubmit`.
   *
   * It decides BOTH halves of the call, which is the whole reason this function exists: on the
   * OTP path the customer is withheld (the verify call carries them) and a plain `status` means
   * the code went out; on the OTP-less path the customer rides along (this request IS the
   * enrolment) and a plain `status` means they are enrolled.
   */
  viaOtp: boolean;
}

/**
 * Ask the backend to enrol this customer, on whichever path their programme selected.
 *
 * 🔴 **The one place the request shape and the response reading are paired.** Four rules live
 * here and nowhere else:
 *
 * | rule | why it cannot live in the caller |
 * |---|---|
 * | `customerInfo` rides along only when NO verify call will follow | on the OTP path the verify request is what enrols; sending the customer twice is not the problem, sending it *neither* time is |
 * | the classifier is chosen by path | the same `{status:true}` means "code sent" on one and "enrolled" on the other |
 * | `otpSent` overrules our own prediction | the backend may send a code on a request we expected to enrol outright |
 * | a terminal success re-reads the customer | `providerConsents` is what every later "is consent owed" read depends on |
 *
 * Both surfaces went through those four independently before this existed — the POS in
 * `useCustomerConsent`, the kiosk in `ordering/customer` — and the kiosk's copy got the second
 * one wrong, letting a customer through the OTP path unverified whenever the backend omitted
 * `otpSent`. One definition, one place to fix.
 *
 * Callback-style rather than a promise because the POS's form drives from callbacks; the kiosk
 * wraps it in one line where it wants `await`.
 */
export function requestEnrolment(
  { phoneNumber, provider, customerInfo, viaOtp }: EnrolmentRequest,
  cb: (outcome: EnrolmentOutcome) => void,
): void {
  sendConsentOtp(
    // Withheld on the OTP path — the verify call is what carries the customer there.
    { phoneNumber, provider, customerInfo: viaOtp ? undefined : customerInfo },
    (res: any) => {
      const outcome = viaOtp
        ? toEnrolmentOutcome(classifyConsentOtpResponse(res))
        : toEnrolmentOutcome(classifyEnrolWithoutOtpResponse(res));
      finishIfTerminal(outcome, phoneNumber);
      cb(outcome);
    },
  );
}

/**
 * Confirm an enrolment with the code the customer typed.
 *
 * `customerInfo` is REQUIRED here: on the OTP path this is the only request that carries the
 * customer, so it is what the backend creates and enrols them from.
 */
export function confirmEnrolment(
  { phoneNumber, provider, customerInfo }: ConsentRequest,
  otp: string,
  cb: (outcome: EnrolmentOutcome) => void,
): void {
  verifyConsentOtp({ phoneNumber, provider, customerInfo }, otp, (res: any) => {
    const outcome = toEnrolmentOutcome(classifyConsentVerifyResponse(res));
    finishIfTerminal(outcome, phoneNumber);
    cb(outcome);
  });
}

/** `ConsentOutcome`'s `otp-sent` is this layer's `otp-required` — the rest carry over. */
function toEnrolmentOutcome(outcome: ConsentOutcome): EnrolmentOutcome {
  return outcome === "otp-sent" ? "otp-required" : outcome;
}

/**
 * Re-read the provider's view of the customer once the flow has actually ended.
 *
 * ⚠️ Fires on `already-consented` as well as `enrolled`. The POS used to refresh only on the
 * latter, which left `providerConsents` stale for a customer the backend told us was already
 * enrolled — the very read that decides whether to ask them again. One idempotent GET, and it
 * makes both surfaces consistent.
 *
 * NOT fired on `otp-required`: nothing has changed at the provider yet.
 */
function finishIfTerminal(outcome: EnrolmentOutcome, phoneNumber: string): void {
  if (outcome === "enrolled" || outcome === "already-consented") {
    refreshCustomerLoyalty(phoneNumber);
  }
}

/**
 * Withdraw a customer from the loyalty programme ("Leave program").
 *
 * ⚠️ Consent withdrawal, not a settings toggle — earned points do not carry over if they enrol
 * again. Callers confirm before calling.
 */
export function optOutOfLoyaltyProgram(customerPhone: string, cb: (res: any) => void): void {
  store.dispatch(customerOptOut({ dataToPost: { customerPhone }, callback: cb }));
}

/**
 * Re-read the customer's loyalty + consent state from the provider, by PHONE NUMBER.
 *
 * Every consent success path calls this: the backend has just changed what it holds, and
 * `externalLoyaltyData` is what the enrolled banner, the enrol toggle and the points panel all
 * read. No-ops offline.
 *
 * ⚠️ It is this flow's NAME for `fetchDetailedCustomerInfo(ref, true)`, not a second
 * implementation of it. Both had their own `store.dispatch` and their own offline guard after the
 * DM-146 merge brought the helper into core — the same two lines, in two places, free to drift.
 * The intent is worth a name (`external: true` is not obvious at a call site, and the two consent
 * paths read better for it); the dispatch is not worth a copy.
 */
export function refreshCustomerLoyalty(ref: string | number): void {
  fetchDetailedCustomerInfo(ref, true);
}

/**
 * In-flight `customerRefId`s, so two callers asking the same question at the same moment send
 * one request. Module state, deliberately: the two callers are a React effect and a middleware
 * listener, which have no state to share.
 */
const loyaltyInfoFetchInFlight = new Set<string>();

/**
 * Make sure `customerLoyaltyInfo` is loaded for whoever is on this sale.
 *
 * 🔴 **For the paths that attach a customer WITHOUT going through the search modal** — My
 * Orders' "Edit Items" seeds `saleData` complete with a customer, and nothing along that route
 * ever fetches their loyalty record. Every surface that reads `selectCustomerLoyaltyInfo` (the
 * reward rows in the deals sheet, the redemption panel, the customer summary) then renders
 * against `null` for a customer who does have points.
 *
 * No-ops when there is no customer, when no provider is active for this channel/branch, when the
 * data on hand already belongs to THIS customer, or when a fetch for them is already out.
 *
 * ⚠️ "Already loaded" is a question about WHICH customer, not about presence: the previous
 * customer's `loyaltyInfo` is still sitting in `externalLoyaltyData` after a swap. That is what
 * `externalLoyaltyData.customerRefId` is stamped for — see `saga/pos/customer`.
 *
 * Imperative on purpose: its two callers are `NewOrder`'s mount effect and the `saleUpdate`
 * listener `customerLoyaltyFetchEffect`, and only one of them is React.
 */
export function ensureCustomerLoyaltyInfo(saleData: any): void {
  const customerRefId = saleData?.customerInfo?.customerRefId;
  if (!customerRefId) return;

  const state = store.getState();
  if (!selectActiveLoyaltyProvider(state)) return;

  const isFreshForThisCustomer =
    !!selectCustomerLoyaltyInfo(state) &&
    selectCustomerLoyaltyRefId(state) === customerRefId;
  if (isFreshForThisCustomer) return;

  if (loyaltyInfoFetchInFlight.has(customerRefId)) return;

  loyaltyInfoFetchInFlight.add(customerRefId);
  // `external: false` — `customerRefId` is OUR id, not the provider's phone-number key, which is
  // also what makes the saga stamp it back onto `externalLoyaltyData`.
  fetchDetailedCustomerInfo(customerRefId);
  // `fetchDetailedCustomerInfo` exposes no completion signal, so the guard is released on the
  // next tick. It exists to collapse the two synchronous callers, not to dedupe across the
  // request's lifetime — holding it until a response that may never arrive would wedge the
  // customer for the rest of the session.
  setTimeout(() => loyaltyInfoFetchInFlight.delete(customerRefId), 0);
}

/* ── public surface ─────────────────────────────────────────────────────────────────── */

export * from "./types";
export * from "./constants";
export * from "./provider";
export * from "./program";
export * from "./panelModel";
export * from "./programDisplay";
export * from "./faqs";
export * from "./redemption";
export * from "./rewards";
export * from "./consent";
export * from "./selectors";
export * from "./profiles";
